import type { FontVariantDescriptor } from "./fontDefinitions";

type FontStyle = FontVariantDescriptor["style"];

type StyleWeights = {
  normal: Set<number>;
  italic: Set<number>;
};

interface GoogleFontFamilyConfig {
  subsets?: string[];
  display?: "auto" | "block" | "swap" | "fallback" | "optional";
}

interface ParsedFontFace {
  family: string;
  style: FontStyle;
  weight: string;
  srcUrl: string;
  format: string;
  unicodeRange?: string;
}

const DEFAULT_FONT_CONFIG: Required<GoogleFontFamilyConfig> = {
  subsets: ["latin"],
  display: "swap",
};

const GOOGLE_FONT_CONFIGS: Record<string, GoogleFontFamilyConfig> = {
  Changa: { subsets: ["arabic", "latin", "latin-ext"], display: "swap" },
  "M PLUS 1p": { subsets: ["japanese", "latin"], display: "swap" },
  Stylish: { subsets: ["korean", "latin"], display: "swap" },
  Rubik: { subsets: ["latin", "latin-ext", "cyrillic", "cyrillic-ext"], display: "swap" },
  "ZCOOL XiaoWei": { subsets: ["chinese-simplified", "latin"], display: "swap" },
  Montserrat: { subsets: ["latin"], display: "swap" },
  Poppins: { subsets: ["latin"], display: "swap" },
  "Red Rose": { subsets: ["latin"], display: "swap" },
  Quicksand: { subsets: ["latin"], display: "swap" },
};

const cssResponseCache = new Map<string, Promise<string>>();
const fontFaceLoadCache = new Map<string, Promise<void>>();

/**
 * Load the Google Fonts families required for the provided variants.
 */
export async function loadGoogleFontsForVariants(
  variants: FontVariantDescriptor[],
  fontFaceSet: FontFaceSet
): Promise<void> {
  if (typeof fetch !== "function") {
    console.warn("[@universe/fonts] Cannot load Google Fonts: fetch API unavailable in this environment.");
    return;
  }

  const families = groupVariantsByFamily(variants);
  const loadPromises = Array.from(families.entries()).map(([family, styleWeights]) =>
    loadGoogleFontFamily(family, styleWeights, fontFaceSet)
  );

  await Promise.all(loadPromises);
}

function groupVariantsByFamily(
  variants: FontVariantDescriptor[]
): Map<string, StyleWeights> {
  const families = new Map<string, StyleWeights>();
  for (const variant of variants) {
    let entry = families.get(variant.family);
    if (!entry) {
      entry = { normal: new Set<number>(), italic: new Set<number>() };
      families.set(variant.family, entry);
    }

    const target = variant.style === "italic" ? entry.italic : entry.normal;
    target.add(variant.weight);
  }

  return families;
}

async function loadGoogleFontFamily(
  family: string,
  styles: StyleWeights,
  fontFaceSet: FontFaceSet
): Promise<void> {
  const requestUrl = buildGoogleFontsUrl(family, styles);
  if (!requestUrl) {
    return;
  }

  try {
    const css = await fetchCss(requestUrl);
    const parsedFaces = parseFontFaceCss(css);

    const loadTasks = parsedFaces
      .filter((face) => face.family === family)
      .map((face) => loadFontFace(fontFaceSet, face));

    await Promise.all(loadTasks);
  } catch (error) {
    console.warn(`[@universe/fonts] Failed to load Google Font family ${family}:`, error);
  }
}

function buildGoogleFontsUrl(family: string, styles: StyleWeights): string | null {
  const familyQuery = buildFamilyQuery(family, styles);
  if (!familyQuery) {
    console.warn(
      `[@universe/fonts] Skipping Google Font request for ${family}; no weights registered.`
    );
    return null;
  }

  const config = GOOGLE_FONT_CONFIGS[family];
  if (!config) {
    console.warn(
      `[@universe/fonts] Using default Google Font subset configuration for ${family}. ` +
        "Update @universe/fonts if this font requires additional subsets."
    );
  }

  const subsets = (config?.subsets?.length ? config.subsets : DEFAULT_FONT_CONFIG.subsets) ?? [];
  const subsetParam = subsets.length
    ? `&subset=${subsets.map((subset) => encodeURIComponent(subset)).join(",")}`
    : "";
  const displayParam = config?.display ?? DEFAULT_FONT_CONFIG.display;

  return `https://fonts.googleapis.com/css2?family=${familyQuery}&display=${displayParam}${subsetParam}`;
}

function buildFamilyQuery(family: string, styles: StyleWeights): string | null {
  const normalWeights = Array.from(styles.normal).sort((a, b) => a - b);
  const italicWeights = Array.from(styles.italic).sort((a, b) => a - b);

  if (normalWeights.length === 0 && italicWeights.length === 0) {
    return null;
  }

  const encodedFamily = encodeFamilyName(family);

  if (italicWeights.length === 0) {
    return `${encodedFamily}:wght@${normalWeights.join(";")}`;
  }

  const italicEntries = italicWeights.map((weight) => `1,${weight}`);

  if (normalWeights.length === 0) {
    return `${encodedFamily}:ital,wght@${italicEntries.join(";")}`;
  }

  const normalEntries = normalWeights.map((weight) => `0,${weight}`);
  const combined = normalEntries.concat(italicEntries);

  return `${encodedFamily}:ital,wght@${combined.join(";")}`;
}

function encodeFamilyName(family: string): string {
  return family.trim().replace(/\s+/g, "+");
}

async function fetchCss(url: string): Promise<string> {
  const cached = cssResponseCache.get(url);
  if (cached) {
    return cached;
  }

  const request = fetch(url, {
    mode: "cors",
    credentials: "omit",
    headers: {
      Accept: "text/css,*/*;q=0.1",
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Google Fonts request failed with status ${response.status}`);
    }
    return response.text();
  });

  cssResponseCache.set(url, request);
  return request;
}

function parseFontFaceCss(css: string): ParsedFontFace[] {
  const faces: ParsedFontFace[] = [];
  const blockRegex = /@font-face\s*\{([\s\S]*?)\}/gi;
  let match: RegExpExecArray | null;

  while ((match = blockRegex.exec(css)) !== null) {
    const block = match[1];
    const familyMatch = /font-family:\s*["']([^"']+)["']/i.exec(block);
    const styleMatch = /font-style:\s*([^;]+)/i.exec(block);
    const weightMatch = /font-weight:\s*([^;]+)/i.exec(block);
    const srcMatch = /src:\s*([^;]+);/i.exec(block);

    if (!familyMatch || !styleMatch || !weightMatch || !srcMatch) {
      continue;
    }

    const urlMatch = /url\(([^)]+)\)/i.exec(srcMatch[1]);
    const formatMatch = /format\(["']([^"']+)["']\)/i.exec(srcMatch[1]);
    const unicodeMatch = /unicode-range:\s*([^;]+);/i.exec(block);

    if (!urlMatch) {
      continue;
    }

    faces.push({
      family: familyMatch[1],
      style: (styleMatch[1].trim() as FontStyle) ?? "normal",
      weight: weightMatch[1].trim(),
      srcUrl: urlMatch[1].trim().replace(/^"|"$/g, ""),
      format: formatMatch ? formatMatch[1].trim() : "woff2",
      unicodeRange: unicodeMatch ? unicodeMatch[1].trim() : undefined,
    });
  }

  return faces;
}

async function loadFontFace(fontFaceSet: FontFaceSet, face: ParsedFontFace): Promise<void> {
  const key = `${face.family}|${face.style}|${face.weight}|${face.unicodeRange ?? "all"}|${face.srcUrl}`;
  let promise = fontFaceLoadCache.get(key);
  if (!promise) {
    const descriptors: FontFaceDescriptors = {
      style: face.style,
      weight: face.weight,
    };

    if (face.unicodeRange) {
      descriptors.unicodeRange = face.unicodeRange;
    }

    const fontFace = new FontFace(
      face.family,
      `url(${face.srcUrl}) format('${face.format}')`,
      descriptors
    );

    promise = fontFace
      .load()
      .then((loadedFace) => {
        fontFaceSet.add(loadedFace);
        document.body?.classList.add("fonts-loaded");
      })
      .catch((error) => {
        console.warn(
          `[@universe/fonts] Failed to load font ${face.family} ${face.weight} (${face.style}):`,
          error
        );
      });

    fontFaceLoadCache.set(key, promise);
  }

  await promise;
}
